/**
 * TextValueSelector Component
 *
 * Provides a combobox with autocomplete for text/email/url fields.
 * Supports both single and multiple value selection with custom values.
 *
 * @module FilterEditor/TextValueSelector
 */

import React, { useMemo } from 'react';
import { X, ChevronsUpDown } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@loader/ui';

import type { ValueSelectorProps, LinkedTableFieldValuesResponse, FieldValueOption } from './types';

import { useRpc } from '@/lib/rpc';
import { queryKeys } from '@/lib/queryKeys';
import { FIELD_VALUES_CACHE } from '@/config/cacheConfig';
import { constructApiFieldId, determineSourceType } from '@/utils/fieldIdHelpers';

export const TextValueSelector: React.FC<ValueSelectorProps> = ({
  canEdit = true,
  filter,
  selectedField,
  linkedTableId,
  versionSelections,
  debouncedOnUpdate,
}) => {
  const [open, setOpen] = React.useState(false);
  const [searchValue, setSearchValue] = React.useState('');
  const rpc = useRpc();
  const queryClient = useQueryClient();

  /**
   * Construct the proper field ID for API calls
   */
  const apiFieldId = useMemo(() => {
    return constructApiFieldId(filter.field_id, selectedField);
  }, [selectedField, filter.field_id]);

  /**
   * Build query key for field values cache
   */
  const fieldValuesQueryKey = useMemo(() => {
    if (!linkedTableId || !apiFieldId) return undefined;
    return queryKeys.linkedTables.fieldValues(
      linkedTableId,
      apiFieldId,
      versionSelections
    );
  }, [linkedTableId, apiFieldId, versionSelections]);

  /**
   * Check if we have cached data in React Query cache
   */
  const cachedData = React.useMemo(() => {
    if (!open || !linkedTableId || !apiFieldId || !fieldValuesQueryKey) return undefined;
    return queryClient.getQueryData<LinkedTableFieldValuesResponse>(
      fieldValuesQueryKey
    );
  }, [open, linkedTableId, apiFieldId, fieldValuesQueryKey, queryClient]);

  /**
   * Fetch unique values for the selected field from the API
   */
  const {
    data: fieldValuesResponse,
    isLoading: isLoadingValues,
    error: valuesError,
  } = useQuery<LinkedTableFieldValuesResponse>({
    queryKey: fieldValuesQueryKey ?? [],
    queryFn: async () => {
      if (!linkedTableId || !apiFieldId) {
        throw new Error('LinkedTable ID and field ID are required');
      }
      const sourceType = determineSourceType(selectedField);

      const query: Record<string, string> = {};
      if (versionSelections) {
        for (const [alias, selection] of Object.entries(versionSelections)) {
          query[`versionSelections[${alias}]`] = selection;
        }
      }
      if (sourceType) {
        query.sourceType = sourceType;
      }

      // @ts-expect-error - Hono RPC type inference limitation
      const res = await rpc.api.private['linked-tables'][':id']['field-values'][':fieldId'].$get({
        param: { id: linkedTableId, fieldId: apiFieldId },
        query: query as Record<string, string>,
      });

      if (!res.ok) throw new Error('Failed to fetch field values');
      const response = await res.json();

      if ('error' in response) {
        throw new Error(response.error || 'Failed to fetch field values');
      }

      return response;
    },
    // API now supports calculated columns via formula evaluation
    enabled: open && !!linkedTableId && !!apiFieldId && !!selectedField && !!fieldValuesQueryKey,
    placeholderData: cachedData,
    staleTime: FIELD_VALUES_CACHE.STALE_TIME,
    gcTime: FIELD_VALUES_CACHE.GC_TIME,
  });

  /**
   * Get the effective field values data
   */
  const fieldValuesData = fieldValuesResponse || cachedData;

  /**
   * Get possible values from cache or API response
   */
  const possibleValues = useMemo(() => {
    if (!fieldValuesData || !('values' in fieldValuesData)) {
      return [];
    }
    return fieldValuesData.values || [];
  }, [fieldValuesData]);

  /**
   * Filter possible values based on search input
   */
  const filteredValues = useMemo(() => {
    if (!searchValue.trim()) {
      return possibleValues;
    }
    const lowerSearch = searchValue.toLowerCase();
    return possibleValues.filter(
      (option) =>
        option.label.toLowerCase().includes(lowerSearch) ||
        option.value.toLowerCase().includes(lowerSearch)
    );
  }, [possibleValues, searchValue]);

  /**
   * Check if search value matches any existing option
   */
  const hasExactMatch = useMemo(() => {
    if (!searchValue.trim()) return true;
    const lowerSearch = searchValue.toLowerCase();
    return possibleValues.some(
      (option) =>
        option.label.toLowerCase() === lowerSearch ||
        option.value.toLowerCase() === lowerSearch
    );
  }, [possibleValues, searchValue]);

  /**
   * Check if current operator supports multiple values
   */
  const supportsMultipleValues = filter.operator === 'in' || filter.operator === 'not_in';

  /**
   * Handle adding a custom value that's not in the list
   */
  const handleAddCustomValue = () => {
    if (!searchValue.trim()) return;
    handleValueToggle(searchValue.trim());
    setSearchValue('');
  };

  /**
   * Handle single value selection
   */
  const handleSingleValueSelect = (value: string) => {
    debouncedOnUpdate({
      ...filter,
      value: value,
      field_name: selectedField?.name || filter.field_name,
    });
    setSearchValue('');
    setOpen(false);
  };

  /**
   * Handle multiple value selection with checkboxes
   */
  const handleValueToggle = (value: string) => {
    const currentValues = Array.isArray(filter.value)
      ? filter.value
      : filter.value
        ? [String(filter.value)]
        : [];

    const newValues = currentValues.includes(value)
      ? currentValues.filter((v) => v !== value)
      : [...currentValues, value];

    debouncedOnUpdate({
      ...filter,
      value: newValues.length === 1 ? newValues[0] : newValues,
      field_name: selectedField?.name || filter.field_name,
    });
  };

  /**
   * Get display value for the combobox
   */
  const getDisplayValue = (): string => {
    const values = Array.isArray(filter.value)
      ? filter.value
      : filter.value
        ? [String(filter.value)]
        : [];

    if (values.length === 0) {
      return 'Select values';
    }

    const firstOption = possibleValues.find((opt) => opt.value === String(values[0]));
    const firstLabel = firstOption?.label ?? String(values[0]);

    if (values.length === 1) {
      return firstLabel;
    }

    return `${firstLabel} +${values.length - 1} more`;
  };

  /**
   * Check if a value is selected
   */
  const isValueSelected = (value: string): boolean => {
    if (Array.isArray(filter.value)) {
      return filter.value.includes(value);
    }
    return filter.value === value;
  };

  return (
    <div className="flex gap-1">
      <Popover
        open={open}
        onOpenChange={(isOpen) => {
          setOpen(isOpen);
          if (!isOpen) setSearchValue('');
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className="bg-background min-w-0 flex-1 justify-between"
            disabled={!filter.field_id || (isLoadingValues && !cachedData) || !canEdit}
          >
            <span className="truncate">
              {isLoadingValues && !cachedData ? 'Loading values...' : getDisplayValue()}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[300px] p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search or type custom value..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              {isLoadingValues && !cachedData ? (
                <CommandEmpty>Loading values...</CommandEmpty>
              ) : valuesError ? (
                <CommandEmpty>Error loading values. Please try again.</CommandEmpty>
              ) : filteredValues.length === 0 && !searchValue.trim() ? (
                <CommandEmpty>No values found.</CommandEmpty>
              ) : (
                <CommandGroup>
                  {searchValue.trim() && !hasExactMatch && (
                    <CommandItem
                      value={`add-custom-${searchValue}`}
                      onSelect={() => {
                        if (supportsMultipleValues) {
                          handleAddCustomValue();
                        } else {
                          handleSingleValueSelect(searchValue.trim());
                        }
                      }}
                      className="text-primary"
                    >
                      {supportsMultipleValues && (
                        <Checkbox checked={isValueSelected(searchValue.trim())} className="mr-2" />
                      )}
                      Add "{searchValue.trim()}"
                    </CommandItem>
                  )}
                  {filteredValues.map((option: FieldValueOption) => (
                    <CommandItem
                      key={option.value}
                      value={option.value}
                      onSelect={() => {
                        if (supportsMultipleValues) {
                          handleValueToggle(option.value);
                        } else {
                          handleSingleValueSelect(option.value);
                        }
                      }}
                    >
                      {supportsMultipleValues && (
                        <Checkbox checked={isValueSelected(option.value)} className="mr-2" />
                      )}
                      {option.label}
                    </CommandItem>
                  ))}
                  {filteredValues.length === 0 && searchValue.trim() && hasExactMatch && (
                    <CommandEmpty>No matching values.</CommandEmpty>
                  )}
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      {/* Clear button */}
      <Button
        variant="outline"
        size="icon"
        className="h-9 w-9 shrink-0"
        disabled={
          !canEdit ||
          filter.value === null ||
          filter.value === undefined ||
          filter.value === '' ||
          (Array.isArray(filter.value) && filter.value.length === 0)
        }
        onClick={() => {
          debouncedOnUpdate({
            ...filter,
            value: '',
            field_name: selectedField?.name || filter.field_name,
          });
        }}
        title="Clear value"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
